import { differenceInCalendarDays } from "date-fns";
import { FC } from "react";
import { ValueType } from "rsuite/esm/DateRangePicker/types";
import FormFieldDateRangePicker from "../../camera-admin/video-history/dateRangePicker";
import { getDayColor } from "./dayColors";
import { formatDateLabel } from "./dateUtils";
import { MAX_SELECTED_DATES } from "./useMultiDateTripData";

type Props = {
  selectedDates: string[]; // yyyy-MM-dd, oldest first
  onChange: (dates: string[]) => void;
};

const toDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// every calendar day between start and end (inclusive), capped at
// MAX_SELECTED_DATES as a safety net alongside the picker's own disabledDate
const expandRange = (start: Date, end: Date): string[] => {
  const dates: string[] = [];
  const cursor = new Date(start);
  cursor.setHours(0, 0, 0, 0);
  const endDay = new Date(end);
  endDay.setHours(0, 0, 0, 0);
  while (cursor <= endDay && dates.length < MAX_SELECTED_DATES) {
    dates.push(toDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

export const DateSelector: FC<Props> = ({ selectedDates, onChange }) => {
  const removeDate = (dateKey: string) => {
    // always keep at least one date selected
    if (selectedDates.length <= 1) return;
    onChange(selectedDates.filter((existing) => existing !== dateKey));
  };

  const rangeValue: [Date, Date] | null =
    selectedDates.length > 0
      ? [
          new Date(`${selectedDates[0]}T00:00:00`),
          new Date(`${selectedDates[selectedDates.length - 1]}T00:00:00`),
        ]
      : null;

  return (
    <div className="px-3 pt-3 space-y-2">
      <p className="text-sm font-display font-semibold">
        Select Dates{" "}
        <span className="text-xs font-normal text-heading-gray888">
          (Max {MAX_SELECTED_DATES} days)
        </span>
      </p>

      <FormFieldDateRangePicker
        className="w-full"
        value={rangeValue}
        onChange={(value) => {
          if (!value) return;
          const [start, end] = value;
          onChange(expandRange(start, end));
        }}
        format="MM/dd/yyyy"
        placeholder="Select date range"
        editable={false}
        ranges={[]}
        disabledDate={(
          date: Date,
          selectValue?: ValueType,
          selectedDone?: boolean
        ) => {
          if (date > new Date()) return true;
          const rangeStart = selectValue?.[0];
          if (!selectedDone && rangeStart) {
            const diff = Math.abs(differenceInCalendarDays(date, rangeStart));
            return diff > MAX_SELECTED_DATES - 1;
          }
          return false;
        }}
      />

      {/* a full week of chips wraps to three rows, which on a phone is most
          of the panel and leaves the trip list with no room to scroll in.
          capped and scrollable below md; unbounded from md up, where there's
          space for it. The date picker itself is left alone - putting
          overflow on an ancestor would clip its popup. */}
      {selectedDates.length > 0 && (
        <div className="flex flex-wrap gap-2 max-h-20 overflow-y-auto overscroll-contain md:max-h-none md:overflow-visible">
          {selectedDates.map((dateKey, index) => (
            <div
              key={dateKey}
              className="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium"
              style={{
                backgroundColor: `${getDayColor(index)}1A`,
                color: getDayColor(index),
                border: `1px solid ${getDayColor(index)}55`,
              }}
            >
              {formatDateLabel(dateKey)}
              {selectedDates.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeDate(dateKey)}
                  className="leading-none hover:opacity-60"
                  aria-label={`Remove ${formatDateLabel(dateKey)}`}
                >
                  &times;
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
